import { CoreApiClient } from '../../core/client'
import { withQuery } from '../../core/query'
import type { PageResult, StandardSaasWorkbench } from '../../core/types'
import type { StandardSaasDecoration } from '../decoration'

export interface AlumniProfileQuery {
  keyword?: string
  status?: string
  alumniCategory?: string
  alumniType?: number | null
  certStatus?: number | null
  profileVisibility?: number | null
  city?: string
  className?: string
  ownerUserId?: number | null
  gender?: string
  enrollmentYear?: string
  graduationYear?: string
  pageNum?: number
  pageSize?: number
}

export interface AlumniProfile {
  id: number
  tenantId: number
  name: string
  gender: string
  enrollmentYear: string
  graduationYear: string
  college: string
  major: string
  className: string
  mobile: string
  email: string
  city: string
  company: string
  title: string
  status: string
  alumniCategory: string
  alumniType: number
  certStatus: number
  birthday: string
  employeeNo: string
  gradeNo: string
  profileVisibility: number
  lastActiveAt: string
  updatedAt: string
  ownerUserId?: number | null
  ownerDeptId?: number | null
  createdAt: string
  idCardNo?: string | null
  classId?: string | null
}

export interface CreateAlumniProfileRequest {
  name: string
  gender: string
  enrollmentYear: string
  graduationYear: string
  college: string
  major: string
  className: string
  mobile: string
  email: string
  city: string
  company: string
  title: string
  alumniCategory?: string
  alumniType?: number | null
  certStatus?: number | null
  birthday?: string
  employeeNo?: string
  gradeNo?: string
  profileVisibility?: number | null
  ownerUserId?: number | null
  ownerDeptId?: number | null
  idCardNo?: string | null
  classId?: string | null
}

export interface UpdateAlumniProfileRequest extends Omit<CreateAlumniProfileRequest, 'ownerUserId' | 'ownerDeptId'> {
  status: string
}

export interface AlumniProfileChangeLog {
  id?: number | string
  fieldName?: string
  fieldLabel?: string
  oldValue?: string
  newValue?: string
  operatorName?: string
  operatorId?: number | string
  createdAt?: string
  [key: string]: unknown
}

export interface AlumniClass {
  id: number
  tenantId: number
  className: string
  college: string
  major: string
  gradeYear: string
  counselorName: string
  memberCount: number
  ownerUserId?: number | null
  ownerDeptId?: number | null
  createdAt: string
}

export interface CreateAlumniClassRequest {
  className: string
  college: string
  major: string
  gradeYear: string
  counselorName: string
  memberCount: number
  ownerUserId?: number | null
  ownerDeptId?: number | null
}

export interface UpdateAlumniClassRequest {
  className: string
  college: string
  major: string
  gradeYear: string
  counselorName: string
  memberCount: number
}

export interface AlumniCertification {
  id: number
  realName: string
  idCardNo: string
  mobile: string
  certMethodLabel: string
  certStatus: number
  certStatusLabel: string
  graduationYear: number
  classNo: string
  faceVerifyStatus: number
  intro: string
  auditRemark: string
  idCardFrontUrl: string
  idCardBackUrl: string
  enrollmentYear: number
  idCardType: string
  proofImageUrls: string
  certMethod: string
  reviewerId: number
  alumniProfileId: number
  tenantId: number
  userId: number
  reviewedAt: { seconds: number; nanos: number }
  createdAt: { seconds: number; nanos: number }
  updatedAt: { seconds: number; nanos: number }
}

export interface CertAuditRequest {
  certStatus: number
  auditRemark: string
}

export interface AlumniPointRecord {
  id: number
  changeType: string
  points: number
  beforePoints: number
  afterPoints: number
  source: string
  remark: string
  createdAt: string
}

export interface AlumniGroupType {
  id: number
  name: string
  displayName: string
  iconUrl: string
  code: string
  sort: number
  isEnabled: number
  remark: string
}

export interface CreateAlumniGroupTypeRequest {
  name: string
  displayName: string
  iconUrl: string
  code: string
  sort: number
  isEnabled: number
  remark: string
}

export interface AlumniGroup {
  id: number
  tenantId: number
  groupName: string
  groupType: string
  city: string
  description: string
  memberCount: number
  coverImageUrl: string
  official: boolean
  dynamicCount: number
  activityCount: number
  sortOrder: number
  status: string | number
  statusText?: string
  ownerUserId?: number | null
  ownerDeptId?: number | null
  createdAt: string
}

export interface AlumniGroupQuery {
  keyword?: string
  groupType?: string
  status?: string | number
  pageNum?: number
  pageSize?: number
}

export type AlumniPageResultAlumniGroup = PageResult<AlumniGroup>

export interface AlumniGroupQrCode {
  groupId: number
  publicUrl: string
  imageUrl: string
  status: string
}

export interface AddAlumniGroupMemberRequest {
  alumniProfileId: string
  role: number
}

export interface AlumniGroupMember {
  id: number
  groupId: number
  alumniProfileId: number
  memberName: string
  role: number
  roleText: string
  joinedAt: string
}

export interface CreateAlumniGroupRequest {
  groupName: string
  groupType: string
  city: string
  description: string
  memberCount: number
  coverImageUrl?: string
  official?: boolean
  dynamicCount?: number
  activityCount?: number
  sortOrder?: number
  ownerUserId?: number | null
  ownerDeptId?: number | null
}

export interface UpdateAlumniGroupRequest {
  groupName: string
  groupType: string
  city: string
  description: string
  memberCount: number
  coverImageUrl?: string
  official?: boolean
  dynamicCount?: number
  activityCount?: number
  sortOrder?: number
  status: string | number
}

export interface AlumniEvent {
  id: number
  tenantId: number
  title: string
  category: string
  city: string
  location: string
  eventTime: string
  signupCount: number
  capacity: number
  status: string
  coverImageUrl: string
  summary: string
  ownerUserId?: number | null
  ownerDeptId?: number | null
  createdAt: string
}

export interface CreateAlumniEventRequest {
  title: string
  category: string
  city: string
  location: string
  eventTime: string
  capacity: number
  coverImageUrl: string
  summary: string
  ownerUserId?: number | null
  ownerDeptId?: number | null
}

export interface UpdateAlumniEventRequest extends Omit<CreateAlumniEventRequest, 'ownerUserId' | 'ownerDeptId'> {
  signupCount: number
  status: string
}

export interface AlumniNews {
  id: number
  tenantId: number
  title: string
  category: string
  summary: string
  coverImageUrl: string
  status: string
  publishedAt: string
  ownerUserId?: number | null
  ownerDeptId?: number | null
  createdAt: string
}

export interface CreateAlumniNewsRequest {
  title: string
  category: string
  summary: string
  coverImageUrl: string
  publishedAt: string
  ownerUserId?: number | null
  ownerDeptId?: number | null
}

export interface UpdateAlumniNewsRequest extends Omit<CreateAlumniNewsRequest, 'ownerUserId' | 'ownerDeptId'> {
  status: string
}

export interface AlumniServiceApplication {
  id: number
  tenantId: number
  serviceType: string
  applicantName: string
  applicantUserId?: number | null
  applicantAlumniProfileId?: number | null
  mobile: string
  content: string
  status: string
  ownerDeptId?: number | null
  serviceId?: number | null
  createdAt: string
}

export interface AlumniServiceApplicationQuery {
  keyword?: string
  serviceType?: string
  status?: string | number
  pageNum?: number
  pageSize?: number
}

export interface CreateAlumniServiceApplicationRequest {
  serviceType: string
  applicantName: string
  applicantUserId?: number | null
  applicantAlumniProfileId?: number | null
  mobile: string
  content: string
  ownerDeptId?: number | null
  idempotencyKey?: string | null
}

export interface UpdateAlumniServiceApplicationStatusRequest {
  status: string
  auditRemark?: string
}

export interface AlumniCatalogItemQuery {
  menuCode?: string
  keyword?: string
  status?: number
  category?: string
  ownerName?: string
  mobile?: string
  tag?: string
  source?: string
  occurredFrom?: string
  occurredTo?: string
  pageNum?: number
  pageSize?: number
}

export interface AlumniCatalogItem {
  id: number
  tenantId: number
  menuCode: string
  category: string
  title: string
  ownerName: string
  mobile: string
  status: number
  statusText: string
  metricText: string
  content: string
  occurredAt: string
  priority: number
  source: string
  tags: string
  ownerUserId?: number | null
  ownerDeptId?: number | null
  createdAt: string
  updatedAt: string
}

export interface CreateAlumniCatalogItemRequest {
  menuCode: string
  category: string
  title: string
  ownerName: string
  mobile: string
  status: number
  metricText: string
  content: string
  occurredAt: string
  priority?: number | null
  source?: string | null
  tags?: string | null
  ownerUserId?: number | null
  ownerDeptId?: number | null
}

export interface UpdateAlumniCatalogItemRequest
  extends Omit<CreateAlumniCatalogItemRequest, 'menuCode' | 'ownerUserId' | 'ownerDeptId'> {}

export interface MedalConfig {
  id: number
  tenantId: number
  configKey: string
  configValue: string
  valueType: string
  displayName: string
  description: string
  sort: number
  createdAt: string
  updatedAt: string
}

export interface CreateMedalConfigRequest {
  configKey: string
  configValue: string
  valueType: string
  displayName: string
  description: string
  sort: number
}

export interface UpdateMedalConfigRequest extends Partial<CreateMedalConfigRequest> {}

export interface MedalAward {
  id: number
  tenantId: number
  userId: number
  medalKey: string
  medalName: string
  medalIcon: string
  source: string
  remark: string
  awardedAt: string
  createdAt: string
}

export interface MedalAwardQuery {
  pageNum?: number
  pageSize?: number
}

export interface CreateMedalAwardRequest {
  userId: number
  medalKey: string
  medalName: string
  medalIcon: string
  source: string
  remark: string
}

export interface AlumniCard {
  id: number
  tenantId: number
  userId: number
  alumniProfileId: number
  cardNo: string
  realName: string
  avatar: string
  status: number
  statusText: string
  applyAt: string
  remark: string
  createdAt: string
  updatedAt: string
}

export interface CreateAlumniCardRequest {
  userId: number
  alumniProfileId: number
  cardNo: string
  remark: string
}

export interface UpdateAlumniCardRequest {
  status: number
  remark: string
}

export interface StatOverview {
  alumniTotal: number
  registeredTotal: number
  authenticatedTotal: number
  groupTotal: number
  eventTotal: number
  postTotal: number
  orderTotal: number
  messageTotal: number
}

export interface PopularAppItem {
  appName: string
  visitCount: number
  rank: number
}

export interface ActiveAlumniItem {
  rank: number
  name: string
  activity: number
}

export interface RegionDistItem {
  regionName: string
  userCount: number
  rank: number
}

export interface GroupRankItem {
  rank: number
  groupName: string
  activity: number
  memberCount: number
}

export interface BirthdayItem {
  name: string
  gender: string
  className: string
  age: number
  birthDate: string
}

export interface StatRegion {
  name: string
  value: number
}

export interface AuthDistItem {
  name: string
  value: number
}

export interface AnalyticsDashboard {
  overview: StatOverview
  popularApps: { list: PopularAppItem[] }
  activeAlumni: { list: ActiveAlumniItem[] }
  regionDistribution: { list: RegionDistItem[] }
  groupTop10: { list: GroupRankItem[] }
  birthdayTop10: { list: BirthdayItem[] }
  regions: { list: StatRegion[] }
  authDistribution: { list: AuthDistItem[] }
}

export interface AlumniNamecard {
  id: number
  tenantId: number
  sourceUserId: string
  alumniProfileId: number
  name: string
  mobile: string
  email: string
  company: string
  duty: string
  officePhone: string
  contactAddr: string
  isShowMobile: number
  avatar?: string | null
  viewCount?: string | number
  detailJson: string
  createdAt: string
  updatedAt: string
}

export interface AlumniFeedback {
  id: number
  tenantId: number
  clientUserId: number
  feedbackType: string
  feedbackTypeLabel: string
  content: string
  imageUrls: string
  contact: string
  status: number
  statusText: string
  replyContent: string
  replyUserId: number
  repliedAt: string
  createdAt: string
  updatedAt: string
}

export interface FeedbackQuery {
  pageNum?: number
  pageSize?: number
  feedbackType?: string
  status?: number | string
  keyword?: string
  dateFrom?: string
  dateTo?: string
}

export interface AlumniAlbum {
  id: number
  tenantId: number
  name: string
  coverImage: string
  description: string
  mediaType: number
  photoCount: number
  sort: number
  createdAt: { seconds: number; nanos: number }
}

export interface CreateAlumniAlbumRequest {
  name: string
  coverImage: string
  description: string
  mediaType: number
  sort: number
  isEnabled: number
}

export interface UpdateAlumniAlbumRequest extends Partial<CreateAlumniAlbumRequest> {}

export interface AlumniPhoto {
  id: number
  albumId: number
  title: string
  imageUrl: string
  sort: number
  createdAt: { seconds: number; nanos: number }
}

export interface CreateAlumniPhotoRequest {
  albumId: number
  imageUrl: string
  title: string
  sort: number
}

export interface UpdateAlumniPhotoRequest {
  title?: string
  imageUrl?: string
  sort?: number
}

export interface AlumniDashboard {
  tenantId: number
  profileCount: number
  classCount: number
  groupCount: number
  activeEventCount: number
  publishedNewsCount: number
  pendingServiceCount: number
  upcomingEvents: AlumniEvent[]
  latestNews: AlumniNews[]
  updatedAt: string
}

export interface AlumniHome {
  tenantId: number
  tenantName: string
  slogan: string
  dashboard: AlumniDashboard
  groups: AlumniGroup[]
  events: AlumniEvent[]
  news: AlumniNews[]
  updatedAt: string
}

export interface TenantAlumniStatus {
  tenantId: number
  dashboard: AlumniDashboard
  storageMode: string
}

export interface AlumniPostQuery {
  keyword?: string
  postType?: string
  status?: number
  pageNum?: number
  pageSize?: number
}

export interface AlumniPostAttachmentVO {
  id?: number
  fileUrl?: string
  fileName?: string
  fileSize?: number
  mimeType?: string
  weigh?: number
  createdAt?: string
}

export interface AlumniPostVO {
  id?: number
  tenantId?: number
  title?: string
  summary?: string
  content?: string
  authorUserId?: number
  authorName?: string
  authorAlumniId?: number
  postType?: string
  status?: number
  rejectReason?: string
  reviewerId?: number
  reviewedAt?: string
  viewCount?: number
  weigh?: number
  remark?: string
  createdAt?: string
  updatedAt?: string
  attachments?: AlumniPostAttachmentVO[]
  likeCount?: number
  commentCount?: number
  favoriteCount?: number
  favorited?: boolean
}

export type AlumniPageResultAlumniPost = PageResult<AlumniPostVO>

export interface UpdateAlumniPostRequest {
  title?: string
  summary?: string
  content?: string
  postType?: string
  status?: number
  rejectReason?: string
  weigh?: number
  remark?: string
}

export const ALUMNI_PERMISSIONS = {
  adminInspect: 'platform:alumni:inspect',
  workbenchView: 'alumni:workbench:view',
  decorationView: 'alumni:decoration:view',
  decorationManage: 'alumni:decoration:manage',
  profileView: 'alumni:profile:view',
  profileManage: 'alumni:profile:manage',
  classView: 'alumni:class:view',
  classManage: 'alumni:class:manage',
  groupView: 'alumni:group:view',
  groupManage: 'alumni:group:manage',
  eventView: 'alumni:event:view',
  eventManage: 'alumni:event:manage',
  newsView: 'alumni:news:view',
  newsManage: 'alumni:news:manage',
  serviceView: 'alumni:service:view',
  serviceManage: 'alumni:service:manage',
  catalogView: 'alumni:catalog:view',
  catalogManage: 'alumni:catalog:manage',
  certView: 'alumni:cert:view',
  certManage: 'alumni:cert:manage',
  cardView: 'alumni:card:view',
  cardManage: 'alumni:card:manage',
  namecardView: 'alumni:namecard:view',
  namecardManage: 'alumni:namecard:manage',
  pointsView: 'alumni:points:view',
  pointsManage: 'alumni:points:manage',
  medalView: 'alumni:medal:view',
  medalManage: 'alumni:medal:manage',
  feedbackView: 'alumni:feedback:view',
  feedbackManage: 'alumni:feedback:manage',
  clientHomeView: 'alumni:client:home:view',
  clientProfileView: 'alumni:client:profile:view',
  clientEventView: 'alumni:client:event:view',
  clientNewsView: 'alumni:client:news:view',
  clientGroupView: 'alumni:client:group:view',
  clientServiceApply: 'alumni:client:service:apply',
  clientServiceManage: 'alumni:client:service:manage',
  clientAlbumView: 'alumni:client:album:view',
  clientInboxView: 'alumni:client:inbox:view',
  clientCertApply: 'alumni:client:cert:apply',
  clientCertView: 'alumni:client:cert:view',
  clientCardView: 'alumni:client:card:view',
  clientNamecardView: 'alumni:client:namecard:view',
  clientPointsView: 'alumni:client:points:view',
  clientMedalView: 'alumni:client:medal:view',
  clientFeedbackSubmit: 'alumni:client:feedback:submit',
  clientFeedbackView: 'alumni:client:feedback:view',
  clientPostView: 'alumni:client:post:view',
  clientPostCreate: 'alumni:client:post:create',
  clientPostUpdate: 'alumni:client:post:update',
  clientPostDelete: 'alumni:client:post:delete'
} as const

export const ALUMNI_PERMISSION_LIST = Object.values(ALUMNI_PERMISSIONS)

export class AlumniApiModule {
  private readonly client: CoreApiClient

  constructor(client: CoreApiClient) {
    this.client = client
  }

  getSaasAlumniWorkbench() {
    return this.client.get<StandardSaasWorkbench>('/api/saas/alumni/v2/workbench')
  }

  getSaasAlumniDecoration() {
    return this.client.get<StandardSaasDecoration>('/api/saas/alumni/decoration')
  }

  saveSaasAlumniDecoration(body: StandardSaasDecoration) {
    return this.client.put<StandardSaasDecoration>('/api/saas/alumni/decoration', body)
  }

  getSaasAlumniDashboard() {
    return this.client.get<AlumniDashboard>('/api/saas/alumni/v2/dashboard')
  }

  getSaasAlumniProfiles(query: AlumniProfileQuery = {}) {
    return this.client.get<AlumniProfile[]>(withQuery('/api/saas/alumni/v2/profiles', query))
  }

  getSaasAlumniProfilesPage(query: AlumniProfileQuery = {}) {
    return this.client.get<PageResult<AlumniProfile>>(withQuery('/api/saas/alumni/v2/profiles', query))
  }

  getSaasAlumniProfileChangeLog(profileId: number | string, pageNum = 1, pageSize = 20) {
    return this.client.get<PageResult<AlumniProfileChangeLog>>(
      withQuery(`/api/saas/alumni/v2/profiles/${profileId}/change-log`, { pageNum, pageSize })
    )
  }

  createSaasAlumniProfile(body: CreateAlumniProfileRequest) {
    return this.client.post<AlumniProfile>('/api/saas/alumni/v2/profiles', body)
  }

  updateSaasAlumniProfile(profileId: number, body: UpdateAlumniProfileRequest) {
    return this.client.put<AlumniProfile>(`/api/saas/alumni/v2/profiles/${profileId}`, body)
  }

  deleteSaasAlumniProfile(profileId: number) {
    return this.client.delete<boolean>(`/api/saas/alumni/profiles/${profileId}`)
  }

  getSaasAlumniClasses() {
    return this.client.get<AlumniClass[]>('/api/saas/alumni/v2/classes')
  }

  createSaasAlumniClass(body: CreateAlumniClassRequest) {
    return this.client.post<AlumniClass>('/api/saas/alumni/v2/classes', body)
  }

  updateSaasAlumniClass(classId: number, body: UpdateAlumniClassRequest) {
    return this.client.put<AlumniClass>(`/api/saas/alumni/v2/classes/${classId}`, body)
  }

  getSaasAlumniGroups(query: AlumniGroupQuery = {}) {
    return this.client.get<PageResult<AlumniGroup>>(withQuery('/api/saas/alumni/v2/groups', query))
  }

  getSaasAlumniGroupMembers(groupId: number, pageNum = 1, pageSize = 10) {
    return this.client.get<AlumniGroupMember[]>(
      withQuery(`/api/saas/alumni/v2/groups/${groupId}/members`, { pageNum, pageSize })
    )
  }

  addSaasAlumniGroupMember(groupId: number, body: AddAlumniGroupMemberRequest) {
    return this.client.post<AlumniGroupMember>(`/api/saas/alumni/v2/groups/${groupId}/members`, body)
  }

  getSaasAlumniGroupTypes() {
    return this.client.get<AlumniGroupType[]>('/api/saas/alumni/v2/group-types')
  }

  createSaasAlumniGroupType(body: CreateAlumniGroupTypeRequest) {
    return this.client.post<AlumniGroupType>('/api/saas/alumni/v2/group-types', body)
  }

  updateSaasAlumniGroupType(typeId: number, body: CreateAlumniGroupTypeRequest) {
    return this.client.put<AlumniGroupType>(`/api/saas/alumni/v2/group-types/${typeId}`, body)
  }

  deleteSaasAlumniGroupType(typeId: number) {
    return this.client.delete<void>(`/api/saas/alumni/v2/group-types/${typeId}`)
  }

  getSaasAlumniPoints(pageNum = 1, pageSize = 10) {
    return this.client.get<AlumniPointRecord[]>(withQuery('/api/saas/alumni/v2/points', { pageNum, pageSize }))
  }

  getSaasAlumniCertifications() {
    return this.client.get<AlumniCertification[]>('/api/saas/alumni/v2/certification')
  }

  getSaasAlumniCertificationDetail(certId: number) {
    return this.client.get<AlumniCertification>(`/api/saas/alumni/v2/certification/${certId}`)
  }

  approveSaasAlumniCertification(certId: number, body: CertAuditRequest) {
    return this.client.put<AlumniCertification>(`/api/saas/alumni/v2/certification/${certId}/approve`, body)
  }

  rejectSaasAlumniCertification(certId: number, body: CertAuditRequest) {
    return this.client.put<AlumniCertification>(`/api/saas/alumni/v2/certification/${certId}/reject`, body)
  }

  createSaasAlumniGroup(body: CreateAlumniGroupRequest) {
    return this.client.post<AlumniGroup>('/api/saas/alumni/v2/groups', body)
  }

  updateSaasAlumniGroup(groupId: number, body: UpdateAlumniGroupRequest) {
    return this.client.put<AlumniGroup>(`/api/saas/alumni/v2/groups/${groupId}`, body)
  }

  issueSaasAlumniGroupQrCode(groupId: number) {
    return this.client.post<AlumniGroupQrCode>(`/api/saas/alumni/v2/groups/${groupId}/qrcode`, {})
  }

  getSaasAlumniEvents() {
    return this.client.get<AlumniEvent[]>('/api/saas/alumni/v2/events')
  }

  createSaasAlumniEvent(body: CreateAlumniEventRequest) {
    return this.client.post<AlumniEvent>('/api/saas/alumni/v2/events', body)
  }

  updateSaasAlumniEvent(eventId: number, body: UpdateAlumniEventRequest) {
    return this.client.put<AlumniEvent>(`/api/saas/alumni/v2/events/${eventId}`, body)
  }

  getSaasAlumniNews() {
    return this.client.get<AlumniNews[]>('/api/saas/alumni/v2/news')
  }

  createSaasAlumniNews(body: CreateAlumniNewsRequest) {
    return this.client.post<AlumniNews>('/api/saas/alumni/v2/news', body)
  }

  updateSaasAlumniNews(newsId: number, body: UpdateAlumniNewsRequest) {
    return this.client.put<AlumniNews>(`/api/saas/alumni/v2/news/${newsId}`, body)
  }

  deleteSaasAlumniNews(newsId: number) {
    return this.client.delete<void>(`/api/saas/alumni/v2/news/${newsId}`)
  }

  getSaasAlumniPostsPage(query: AlumniPostQuery = {}) {
    return this.client.get<PageResult<AlumniPostVO>>(withQuery('/api/saas/alumni/v2/posts', query))
  }

  getSaasAlumniPost(postId: number) {
    return this.client.get<AlumniPostVO>(`/api/saas/alumni/v2/posts/${postId}`)
  }

  updateSaasAlumniPost(postId: number, body: UpdateAlumniPostRequest) {
    return this.client.put<AlumniPostVO>(`/api/saas/alumni/v2/posts/${postId}`, body)
  }

  deleteSaasAlumniPost(postId: number) {
    return this.client.delete<void>(`/api/saas/alumni/v2/posts/${postId}`)
  }

  getSaasAlumniServiceApplications(query: AlumniServiceApplicationQuery = {}) {
    return this.client.get<PageResult<AlumniServiceApplication>>(withQuery('/api/saas/alumni/v2/services', query))
  }

  createSaasAlumniServiceApplication(body: CreateAlumniServiceApplicationRequest) {
    return this.client.post<AlumniServiceApplication>('/api/saas/alumni/v2/services/applications', body)
  }

  updateSaasAlumniServiceApplicationStatus(applicationId: number, body: UpdateAlumniServiceApplicationStatusRequest) {
    return this.client.put<AlumniServiceApplication>(`/api/saas/alumni/v2/services/applications/${applicationId}/status`, body)
  }

  getSaasAlumniCatalogItems(query: AlumniCatalogItemQuery = {}) {
    return this.client.get<PageResult<AlumniCatalogItem>>(withQuery('/api/saas/alumni/v2/catalog/items', query))
  }

  createSaasAlumniCatalogItem(body: CreateAlumniCatalogItemRequest) {
    return this.client.post<AlumniCatalogItem>('/api/saas/alumni/v2/catalog/items', body)
  }

  updateSaasAlumniCatalogItem(itemId: number, body: UpdateAlumniCatalogItemRequest) {
    return this.client.put<AlumniCatalogItem>(`/api/saas/alumni/v2/catalog/items/${itemId}`, body)
  }

  deleteSaasAlumniCatalogItem(itemId: number) {
    return this.client.delete<boolean>(`/api/saas/alumni/v2/catalog/items/${itemId}`)
  }

  getSaasAlumniMedalConfigs() {
    return this.client.get<MedalConfig[]>('/api/saas/alumni/v2/medals/config')
  }

  getSaasAlumniMedalConfig(id: number) {
    return this.client.get<MedalConfig>(`/api/saas/alumni/v2/medals/config/${id}`)
  }

  createSaasAlumniMedalConfig(body: CreateMedalConfigRequest) {
    return this.client.post<MedalConfig>('/api/saas/alumni/v2/medals/config', body)
  }

  updateSaasAlumniMedalConfig(id: number, body: UpdateMedalConfigRequest) {
    return this.client.put<MedalConfig>(`/api/saas/alumni/v2/medals/config/${id}`, body)
  }

  deleteSaasAlumniMedalConfig(id: number) {
    return this.client.delete<boolean>(`/api/saas/alumni/v2/medals/config/${id}`)
  }

  getSaasAlumniMedalAwards(query: MedalAwardQuery = {}) {
    return this.client.get<PageResult<MedalAward>>(withQuery('/api/saas/alumni/v2/medals/awards', query))
  }

  createSaasAlumniMedalAward(body: CreateMedalAwardRequest) {
    return this.client.post<MedalAward>('/api/saas/alumni/v2/medals/awards', body)
  }

  deleteSaasAlumniMedalAward(id: number) {
    return this.client.delete<boolean>(`/api/saas/alumni/v2/medals/awards/${id}`)
  }

  getSaasAlumniCards(query: { pageNum?: number; pageSize?: number } = {}) {
    return this.client.get<PageResult<AlumniCard>>(withQuery('/api/saas/alumni/v2/cards', query))
  }

  getSaasAlumniCard(id: number) {
    return this.client.get<AlumniCard>(`/api/saas/alumni/v2/cards/${id}`)
  }

  createSaasAlumniCard(body: CreateAlumniCardRequest) {
    return this.client.post<AlumniCard>('/api/saas/alumni/v2/cards', body)
  }

  updateSaasAlumniCard(id: number, body: UpdateAlumniCardRequest) {
    return this.client.put<AlumniCard>(`/api/saas/alumni/v2/cards/${id}`, body)
  }

  getSaasAlumniAlbums(query: { pageNum?: number; pageSize?: number } = {}) {
    return this.client.get<PageResult<AlumniAlbum>>(withQuery('/api/saas/alumni/v2/albums', query))
  }

  getSaasAlumniAlbum(albumId: number) {
    return this.client.get<AlumniAlbum>(`/api/saas/alumni/v2/albums/${albumId}`)
  }

  createSaasAlumniAlbum(body: CreateAlumniAlbumRequest) {
    return this.client.post<AlumniAlbum>('/api/saas/alumni/v2/albums', body)
  }

  updateSaasAlumniAlbum(albumId: number, body: UpdateAlumniAlbumRequest) {
    return this.client.put<AlumniAlbum>(`/api/saas/alumni/v2/albums/${albumId}`, body)
  }

  deleteSaasAlumniAlbum(albumId: number) {
    return this.client.delete<boolean>(`/api/saas/alumni/v2/albums/${albumId}`)
  }

  getSaasAlumniPhotos(albumId: number, query: { pageNum?: number; pageSize?: number } = {}) {
    return this.client.get<PageResult<AlumniPhoto>>(withQuery(`/api/saas/alumni/v2/albums/${albumId}/photos`, query))
  }

  createSaasAlumniPhoto(albumId: number, body: CreateAlumniPhotoRequest) {
    return this.client.post<AlumniPhoto>(`/api/saas/alumni/v2/albums/${albumId}/photos`, body)
  }

  updateSaasAlumniPhoto(photoId: number, body: UpdateAlumniPhotoRequest) {
    return this.client.put<AlumniPhoto>(`/api/saas/alumni/v2/photos/${photoId}`, body)
  }

  deleteSaasAlumniPhoto(photoId: number) {
    return this.client.delete<boolean>(`/api/saas/alumni/v2/photos/${photoId}`)
  }

  getSaasAlumniFeedbacks(query: FeedbackQuery = {}) {
    return this.client.get<PageResult<AlumniFeedback>>(withQuery('/api/saas/alumni/v2/feedback', query))
  }

  getSaasAlumniFeedback(id: number) {
    return this.client.get<AlumniFeedback>(`/api/saas/alumni/v2/feedback/${id}`)
  }

  replySaasAlumniFeedback(id: number, body: { replyContent: string; status: number }) {
    return this.client.put<AlumniFeedback>(`/api/saas/alumni/v2/feedback/${id}/reply`, body)
  }

  closeSaasAlumniFeedback(id: number) {
    return this.client.put<void>(`/api/saas/alumni/v2/feedback/${id}/close`, {})
  }

  getSaasAlumniNamecards(query: { pageNum?: number; pageSize?: number } = {}) {
    return this.client.get<PageResult<AlumniNamecard>>(withQuery('/api/saas/alumni/v2/namecards', query))
  }

  getSaasAlumniNamecard(id: number) {
    return this.client.get<AlumniNamecard>(`/api/saas/alumni/v2/namecards/${id}`)
  }

  getSaasAlumniStatsAggregate(): Promise<AnalyticsDashboard> {
    return this.client.request<AnalyticsDashboard>('/api/saas/alumni/stats/aggregate', {
      rawResponse: true
    })
  }
}
